import type { Element as SlateElement, Editor } from 'slate';
import { Text, Node } from 'slate';
import { normalizeUrl } from '@halo/utils';
import { repairSelection } from '../../scripts/utils';
import type { LinkElement } from './types';
import { LinkElementType } from './types';

/**
 * protocol: (https?://)?
 * user: (\\w+:\\w+@)?
 * domain: ([0-9a-z-@]+\\.)+[a-z]{2,63}
 * ip: (\\d+\\.){3}\\d+
 * localhost: localhost
 * port: (:\\d{1,5})?
 * path: (/[^/\\s?#]*)*
 * query: ([?#][^\\s]*)?
 * integrality: (?![.:@a-z0-9-]+)
 */

const urlChar =
  '(https?://)?(\\w+:\\w+@)?(([0-9a-z-@]+\\.)+[a-z]{2,63}|(\\d+\\.){3}\\d+|localhost)(:\\d{1,5})?(/[^/\\s?#]*)*([?#][^\\s]*)?(?![.:@a-z0-9-]+)';

const urlAbsoluteReg = new RegExp(`^${urlChar}$`, 'i');

const urlContainReg = new RegExp(urlChar, 'i');

function parsePart(
  str: string,
  callback: (part: string, isUrl: boolean, index: number) => void
): void {
  const urlCharReg = new RegExp(urlChar, 'ig');
  let lastIndex = 0;
  let index = 0;

  let match: RegExpExecArray | null;
  while ((match = urlCharReg.exec(str)) !== null) {
    if (match.index > lastIndex) {
      const nonUrl = str.substring(lastIndex, match.index);
      callback(nonUrl, false, index++);
    }

    const url = match[0];
    callback(url, true, index++);
    lastIndex = match.index + url.length;
  }

  if (lastIndex < str.length) {
    const remaining = str.substring(lastIndex);
    callback(remaining, false, index);
  }
}

export function isLinkElement(element: SlateElement) {
  return element.type === LinkElementType;
}

export function createLinkElement(url: string): LinkElement {
  return {
    type: LinkElementType,
    url: normalizeUrl(url),
    children: [{ text: url }],
  };
}

export function withLinkEditor(editor: Editor) {
  const { isInline, normalizeNode } = editor;

  const rs = repairSelection(editor);

  editor.isInline = (element: SlateElement) => {
    return isLinkElement(element) || isInline(element);
  };

  editor.normalizeNode = (entry) => {
    const [node, path] = entry;
    rs.save();

    if (Text.isText(node)) {
      const text = Node.string(node);

      const isAbsoluteUrl = urlAbsoluteReg.test(text);
      const linkEntry = editor.above({
        at: path,
        match: isLinkElement as any,
      });

      // Change LinkElement url
      if (linkEntry && isAbsoluteUrl) {
        editor.setNodes(
          {
            url: normalizeUrl(text),
          },
          {
            at: linkEntry[1],
          }
        );
        return;
      }

      // Abnormal url LinkElement
      if (linkEntry && !isAbsoluteUrl) {
        editor.unwrapNodes({
          at: path,
          match: isLinkElement as any,
        });
        return;
      }

      // Normal url text
      if (!linkEntry && isAbsoluteUrl) {
        editor.wrapNodes(createLinkElement(text), { at: path });
        return;
      }

      // Contain normal url text
      const isContainUrl = urlContainReg.test(text);
      if (isContainUrl) {
        const nodes: Node[] = [];

        parsePart(text, (part) => {
          nodes.push({ text: part });
        });

        // Insert new nodes
        editor.insertNodes(nodes, {
          at: path,
        });

        // Remove old nodes
        const oldPath = path.slice();
        oldPath[oldPath.length - 1] += nodes.length;
        editor.removeNodes({ at: oldPath });

        rs.repair();
        return;
      }
    }

    rs.clear();

    normalizeNode(entry);
  };

  return editor;
}
